import { api } from "../api";

export type Delegation = {
  id: string;
  scope: "retest" | "report_comment";
  retest_id?: number | null;
  report_id?: number | null;
  from_user_id: number;
  to_user_id: number;
  type: "auto" | "manual";
  status: "pending" | "accepted" | "rejected" | "expired" | "revoked";
  attempt?: number;
  expires_at?: string | null;
  createdAt?: string;
  // Optional included objects from backend
  Retest?: {
    retest_id: number;
    researcher_id: number;
    status: string;
    report?: {
      report_id: number;
      user_id: number;
      program_id: number;
      report_title: string;
      submitted_date: string;
      severity_category: string;
      program?: {
        program_id: number;
        program_title: string;
      };
      user?: {
        user_id: number;
        display_name: string;
        username: string;
      };
    };
  };
  Report?: {
    report_id: number;
    user_id: number;
    program_id: number;
    report_title: string;
    submitted_date: string;
    severity_category: string;
    program?: {
      program_id: number;
      program_title: string;
    };
    user?: {
      user_id: number;
      display_name: string;
      username: string;
    };
  };
};

export const delegationsApi = api.injectEndpoints({
  endpoints: builder => ({
    getMyPendingDelegations: builder.query<{ data: Delegation[] }, void>({
      query: () => ({ url: "/v2/delegations/my-pending", method: "get" }),
      providesTags: [{ type: "Delegations", id: "MY" }]
    }),

    getMyAcceptedDelegations: builder.query<{ data: Delegation[] }, { scope: "retest" | "report_comment" }>({
      query: params => ({ url: "/v2/delegations/my-accepted", method: "get", params }),
      providesTags: [{ type: "Delegations", id: "MY_ACCEPTED" }]
    }),

    // Admin/QA management
    getDelegations: builder.query<
      { data: Delegation[]; count: number; page: number; limit: number },
      Partial<{ page: number; limit: number; status: string; scope: "retest" | "report_comment"; to_user_id: number; from_user_id: number; program_id: number; object_id: number; report_title: string; sort: string; order: "ASC" | "DESC" }>
    >({
      query: params => ({ url: "/v2/delegations", method: "get", params })
    }),

    acceptDelegation: builder.mutation<{ message: string }, { id: string }>({
      query: ({ id }) => ({ url: `/v2/delegations/${id}/accept`, method: "post" }),
      invalidatesTags: [{ type: "Delegations", id: "MY" }]
    }),

    rejectDelegation: builder.mutation<{ message: string }, { id: string }>({
      query: ({ id }) => ({ url: `/v2/delegations/${id}/reject`, method: "post" }),
      invalidatesTags: [{ type: "Delegations", id: "MY" }]
    }),

    revokeDelegation: builder.mutation<{ message: string }, { id: string; reason?: string }>({
      query: ({ id, reason }) => ({ url: `/v2/delegations/${id}/revoke`, method: "post", data: { reason } })
    }),

    deleteDelegation: builder.mutation<{ message: string }, { id: string }>({
      query: ({ id }) => ({ url: `/v2/delegations/${id}`, method: "delete" })
    }),

    createManualDelegation: builder.mutation<
      { message: string; data: Delegation },
      { scope: "retest" | "report_comment"; retest_id?: number; report_id?: number; to_user_id: number; reason?: string }
    >({
      query: body => ({ url: "/v2/delegations/manual-assign", method: "post", data: body }),
      invalidatesTags: [{ type: "Delegations", id: "MY" }]
    }),

    bulkManualAssign: builder.mutation<
      { success: any[]; failed: any[] },
      { assignments: Array<{ scope: "retest" | "report_comment"; retest_id?: number; report_id?: number; to_user_id: number; reason?: string }> }
    >({
      query: body => ({ url: "/v2/delegations/bulk/manual-assign", method: "post", data: body })
    }),

    bulkRevokeDelegations: builder.mutation<{ success: any[]; failed: any[] }, { ids: string[]; reason?: string }>({
      query: body => ({ url: "/v2/delegations/bulk/revoke", method: "post", data: body })
    }),

    bulkDeleteDelegations: builder.mutation<{ success: any[]; failed: any[] }, { ids: string[] }>({
      query: body => ({ url: "/v2/delegations/bulk/delete", method: "post", data: body })
    }),

    getObjectDelegationsStatus: builder.query<
      { data: Delegation[] },
      { scope: "retest" | "report_comment"; retest_id?: number; report_id?: number }
    >({
      query: params => ({ url: "/v2/delegations/object-status", method: "get", params }),
      providesTags: (_res, _err, arg) => [{ type: "Delegations", id: `${arg.scope}-${arg.retest_id || arg.report_id}` }]
    }),

    // Auto-delegation monitoring endpoints
    getAutoDelegationStats: builder.query<{
      total_retests_scanned: number;
      retests_delegated: number;
      retests_skipped: number;
      retests_failed: number;
      total_reports_scanned: number;
      reports_delegated: number;
      reports_skipped: number;
      reports_failed: number;
      total_escalations: number;
      last_scan_time: string;
    }, void>({
      query: () => ({ url: "/v2/delegations/auto/stats", method: "get" }),
      transformResponse: (response: any) => response.data,
      providesTags: [{ type: "Delegations", id: "AUTO_STATS" }]
    }),

    getAutoDelegationHistory: builder.query<
      { data: any[] },
      { type?: "retest" | "report_comment"; status?: "pending" | "accepted" | "rejected" | "expired" | "revoked"; limit?: number; offset?: number }
    >({
      query: params => ({ url: "/v2/delegations/auto/history", method: "get", params }),
      providesTags: [{ type: "Delegations", id: "AUTO_HISTORY" }]
    })
  })
});

export const {
  useGetMyPendingDelegationsQuery,
  useGetMyAcceptedDelegationsQuery,
  useGetDelegationsQuery,
  useAcceptDelegationMutation,
  useRejectDelegationMutation,
  useRevokeDelegationMutation,
  useDeleteDelegationMutation,
  useCreateManualDelegationMutation,
  useBulkManualAssignMutation,
  useBulkRevokeDelegationsMutation,
  useBulkDeleteDelegationsMutation,
  useGetObjectDelegationsStatusQuery,
  useGetAutoDelegationStatsQuery,
  useGetAutoDelegationHistoryQuery
} = delegationsApi;

